import type { ReactNode } from 'react'
import type { StockRow } from '@/lib/market/types'

import { useQuote } from '@/components/market/live-provider'
import { formatCompact, formatKes, formatMultiple, formatNumber, formatPercent, formatPrice } from '@/lib/market/format'
import RangeBar from '@/components/market/range-bar'

interface KeyDataProps {
    stock: StockRow
    capRank: number
    listedCount: number
}

const KeyData = ({ stock, capRank, listedCount }: KeyDataProps): ReactNode => {
    const quote = useQuote(stock.live)
    const cap = quote.last * stock.shares
    const pe = stock.eps !== null && stock.eps > 0 ? quote.last / stock.eps : null
    const dividendYield = stock.dps && quote.last > 0 ? (stock.dps / quote.last) * 100 : null
    const payout = stock.dps !== null && stock.eps !== null && stock.eps > 0 ? (stock.dps / stock.eps) * 100 : null
    const low = Math.min(stock.low52, quote.low)
    const high = Math.max(stock.high52, quote.high)

    const rows: Array<{ label: string, value: ReactNode, hint?: string }> = [
        { label: 'Market capitalisation', value: formatKes(cap), hint: `Rank ${capRank} of ${listedCount}` },
        { label: 'Shares in issue', value: formatCompact(stock.shares) },
        { label: 'Earnings per share', value: stock.eps !== null ? `KES ${formatPrice(stock.eps)}` : '-' },
        { label: 'Price to earnings', value: pe !== null ? formatMultiple(pe) : 'n/m', hint: pe === null && stock.eps !== null ? 'Earnings are negative' : undefined },
        { label: 'Dividend per share', value: stock.dps ? `KES ${formatPrice(stock.dps)}` : 'None declared' },
        { label: 'Dividend yield', value: dividendYield !== null ? formatPercent(dividendYield, 2, false) : '-' },
        { label: 'Payout ratio', value: payout !== null ? formatPercent(payout, 0, false) : '-' },
        { label: 'Earnings yield', value: pe ? formatPercent(100 / pe, 1, false) : '-' },
        { label: 'Average volume, 30 sessions', value: formatCompact(stock.avgVolume30) },
        { label: 'Average turnover, 30 sessions', value: formatKes(stock.avgTurnover30) },
        { label: 'Beta to KASI, 12 months', value: stock.beta !== null ? formatNumber(stock.beta, 2) : '-' },
        { label: 'Volatility, 12 months', value: stock.volatility !== null ? formatPercent(stock.volatility, 1, false) : '-' }
    ]

    return (
        <div>
            <dl className='divide-y divide-border/70'>
                {rows.map(row => (
                    <div key={row.label} className='flex items-baseline justify-between gap-4 py-2 text-[13px]'>
                        <dt className='text-muted-foreground'>{row.label}</dt>
                        <dd className='text-right'>
                            <span className='font-medium tabular-nums text-foreground'>{row.value}</span>
                            {row.hint && <span className='block text-[11px] text-muted-foreground'>{row.hint}</span>}
                        </dd>
                    </div>
                ))}
            </dl>

            <div className='mt-4 rounded-lg border border-border bg-background/50 p-3'>
                <div className='mb-2 flex items-center justify-between text-xs'>
                    <span className='text-muted-foreground'>52-week range</span>
                    <span className='tabular-nums text-foreground'>{formatPercent(high > low ? ((quote.last - low) / (high - low)) * 100 : 50, 0, false)} of range</span>
                </div>
                <RangeBar low={low} high={high} value={quote.last} showLabels />
            </div>
        </div>
    )
}

export default KeyData
